/**
 * referenceBackrefs — AP6. Walk a report's narrative for inline
 * `[Ref N]` tokens and, for each citation, record the section
 * heading that contains it. The appendix renders the resulting
 * `Cited in §X, §Y` strip under each reference row.
 *
 * Implementation note: a SiteRagReport carries narrative across
 * dozens of typed fields. Rather than enumerate them, we treat any
 * string value reachable from the report's typed sections as fair
 * game and tag the citation with the section's `defaultLabel`.
 *
 * The walker is pure: it accepts a `report` and `sections` list and
 * returns a Map<refIndex(1-based), section labels[]>. The render
 * surface (`ReferencesSection.tsx`) reads this map and stitches it
 * into each row.
 */
import type { SiteRagReport } from '../types/siteRag';
import { getSectionsForType, type SectionDef } from '../config/reportSections';

/** 1-based ref index → ordered, deduped list of section labels it
 *  appears in. */
export type CitedInMap = Map<number, string[]>;

/**
 * Build the `Cited in §X, §Y` map for a report. The body text used to
 * decide "what section is this ref in?" is the union of all string
 * values on the report's typed section fields. Section IDs that don't
 * correspond to a direct report property fall back to the section's
 * `defaultLabel` and walk a heuristic subtree (e.g. `executiveSummary`
 * pulls `report.executiveSummary`).
 */
export function buildCitedInMap(report: SiteRagReport): CitedInMap {
  const sections = getSectionsForType(report.reportType);
  const out: CitedInMap = new Map();
  for (const section of sections) {
    const body = extractSectionText(report, section);
    if (!body) continue;
    const matches = body.matchAll(/\[Ref\s+(\d+)\]/gi);
    for (const m of matches) {
      const idx = Number.parseInt(m[1]!, 10);
      if (!Number.isFinite(idx) || idx < 1) continue;
      const existing = out.get(idx) ?? [];
      const label = section.defaultLabel;
      if (!existing.includes(label)) existing.push(label);
      out.set(idx, existing);
    }
  }
  return out;
}

/**
 * Pull the narrative text for one section out of the report. We don't
 * enumerate every section-id-to-field mapping — there are too many —
 * so we use the section's id as a property name probe, falling back
 * to a heuristic for known composite sections.
 */
function extractSectionText(report: SiteRagReport, section: SectionDef): string {
  const sid = section.sectionId;
  // Direct top-level string property — covers `executiveSummary`,
  // `summary`, etc.
  const direct = (report as unknown as Record<string, unknown>)[sid];
  if (typeof direct === 'string') return direct;

  // Common composite-section property names. Order matters — first
  // match wins. The list is conservative: any unknown section just
  // returns ''.
  const candidates: Array<keyof SiteRagReport> = [
    'summary', 'executiveSummary',
  ];
  for (const k of candidates) {
    const v = report[k];
    if (typeof v === 'string' && sid === 'executiveSummary') return v;
  }

  // Walk known typed subtrees by section id.
  const subtrees: Record<string, () => string> = {
    projectIntentOverview: () => {
      const r = report as SiteRagReport;
      return r.projectIntentOverview ?? '';
    },
    keyFindings: () => arrayJoin((report as unknown as Record<string, unknown>)['keyFindings']),
    conclusion: () => objJoin(report.conclusion as unknown),
    riskAssessment: () => arrayJoin(report.risks as unknown),
    actionPlan: () => arrayJoin(report.recommendations as unknown),
    sitingNotes: () => arrayJoin((report as unknown as Record<string, unknown>)['sitingNotes']),
    gridStudy: () => objJoin((report as unknown as Record<string, unknown>)['gridStudy']),
    transportStudy: () => objJoin((report as unknown as Record<string, unknown>)['transportStudy']),
    connectivityStudy: () => objJoin((report as unknown as Record<string, unknown>)['connectivityStudy']),
    climateSevereWeather: () => objJoin((report as unknown as Record<string, unknown>)['climateSevereWeather']),
    extendedData: () => objJoin((report as unknown as Record<string, unknown>)['extendedData']),
    btmOverview: () => objJoin((report as unknown as Record<string, unknown>)['btmOverview']),
    nepaScreening: () => objJoin((report as unknown as Record<string, unknown>)['nepaScreening']),
  };
  const pull = subtrees[sid];
  return pull ? pull() : '';
}

/** Stringify an unknown that may be a string[] / object[] / object —
 *  used to feed the regex matcher. */
function arrayJoin(v: unknown): string {
  if (!Array.isArray(v)) return '';
  return v.map(item => {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return Object.values(item as Record<string, unknown>)
      .filter(x => typeof x === 'string').join(' ');
    return '';
  }).join(' ');
}

function objJoin(v: unknown): string {
  if (!v || typeof v !== 'object') return '';
  if (Array.isArray(v)) return arrayJoin(v);
  return Object.values(v as Record<string, unknown>)
    .map(x => {
      if (typeof x === 'string') return x;
      if (Array.isArray(x)) return arrayJoin(x);
      if (x && typeof x === 'object') return Object.values(x as Record<string, unknown>)
        .filter(y => typeof y === 'string').join(' ');
      return '';
    }).join(' ');
}
